import { metinOku, sayiOku, tarihMetni, yuvarla2 } from '../rapor-ekrani/bicim';

/**
 * S340 Hamam Performans: ds satırlarının ekran karşılıkları ve ekranın SAF hesapları (toplama, ortalama
 * kalış, zaman kovaları, satılan ürün toplamı, süre metni). Kolon adları VERİ SÖZLEŞMESİDİR (SQL ayrı
 * yazılıyor); ham satır burada bir kez okunur, ekran ham alana dokunmaz.
 */

/** 'K' Bayan, 'E' Erkek, '-' kabine bağlı olmayan satış. */
export type Cins = 'K' | 'E' | '-';
/** Taraf süzgeci: 'T' Tümü. İSTEMCİDE süzer, sorgu yinelenmez. */
export type Taraf = 'T' | 'K' | 'E';
export type BolumSecimi = 'T' | 'Salon' | 'VİP' | 'Giriş' | 'Kabinsiz';
export type Donem = 'bugun' | 'dun' | 'son7' | 'ay' | 'ozel';

export const CINS_ADI: Record<Cins, string> = { K: 'Bayan', E: 'Erkek', '-': 'Kabinsiz' };

export const DONEM_SECENEKLERI: ReadonlyArray<{ key: Donem; label: string }> = [
  { key: 'bugun', label: 'Bugün' },
  { key: 'dun', label: 'Dün' },
  { key: 'son7', label: 'Son 7 gün' },
  { key: 'ay', label: 'Bu ay' },
  { key: 'ozel', label: 'Özel' },
];

export const TARAF_SECENEKLERI: ReadonlyArray<{ key: Taraf; label: string }> = [
  { key: 'T', label: 'Tümü' },
  { key: 'K', label: 'Bayan' },
  { key: 'E', label: 'Erkek' },
];

export const BOLUM_SECENEKLERI: ReadonlyArray<{ key: BolumSecimi; label: string }> = [
  { key: 'T', label: 'Tümü' },
  { key: 'Salon', label: 'Salon' },
  { key: 'VİP', label: 'VİP' },
  { key: 'Giriş', label: 'Giriş kasası' },
  { key: 'Kabinsiz', label: 'Kabinsiz' },
];

const KABINSIZ = 'Kabinsiz';
/** Taraf kartındaki bölüm satırlarının sırası. */
const KART_BOLUMLERI = ['Giriş', 'Salon', 'VİP'] as const;

const cinsOku = (v: unknown): Cins => (v === 'K' || v === 'E' ? v : '-');
const iki = (n: number) => String(n).padStart(2, '0');

// ── biçim ───────────────────────────────────────────────────────────────────────

/** Dakika -> "45 dk" / "1 sa 57 dk". Ölçü yoksa "-". */
export function sureMetni(dk: number | null | undefined): string {
  if (dk === null || dk === undefined || !Number.isFinite(dk)) return '-';
  const d = Math.round(dk);
  return d >= 60 ? `${Math.floor(d / 60)} sa ${d % 60} dk` : `${d} dk`;
}

/** Pay, tam yüzdeye yuvarlanmış: "%27". Payda 0 ise "%0". */
export function payMetni(pay: number, payda: number): string {
  return payda ? `%${Math.round((pay / payda) * 100)}` : '%0';
}

/**
 * Zaman damgasının saati ('HH:MM'). `new Date()` ile ÇEVRİLMEZ: ayna saatleri İstanbul lokali, metnin
 * kendisi okunur (bkz. `bicim.tarihMetni`).
 */
export function saatMetni(v: string | null | undefined): string {
  const m = v ? /[T ](\d{2}):(\d{2})/.exec(v) : null;
  return m ? `${m[1]}:${m[2]}` : '';
}

/** "03.10 14:25" (kabin tablosundaki son kapanış). */
export function gunSaatMetni(v: string | null | undefined): string {
  const m = v ? /^\d{4}-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? `${m[2]}.${m[1]} ${saatMetni(v)}`.trim() : '';
}

export function aralikMetni(bas: string, bit: string): string {
  return bas === bit ? tarihMetni(bas) : `${tarihMetni(bas)} - ${tarihMetni(bit)}`;
}

// ── dönem ───────────────────────────────────────────────────────────────────────

/** 'YYYY-MM-DD' + n gün (UTC üzerinden: yaz saati sürprizi yok). */
export function gunEkle(iso: string, n: number): string {
  const [y, a, g] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(y, a - 1, g + n));
  return `${d.getUTCFullYear()}-${iki(d.getUTCMonth() + 1)}-${iki(d.getUTCDate())}`;
}

/** Dönem sekmesinin tarih aralığı (bitiş günü dahil). "Özel" aralık üretmez: tarihler elle girilir. */
export function donemAraligi(donem: Donem, bugun: string): { bas: string; bit: string } | null {
  switch (donem) {
    case 'bugun': return { bas: bugun, bit: bugun };
    case 'dun': return { bas: gunEkle(bugun, -1), bit: gunEkle(bugun, -1) };
    case 'son7': return { bas: gunEkle(bugun, -6), bit: bugun };
    case 'ay': return { bas: `${bugun.slice(0, 8)}01`, bit: bugun };
    default: return null;
  }
}

// ── özet: kabin / kasa satırları ────────────────────────────────────────────────

/** `ozet` ds'i, parametreler `@BASTARIH`, `@BITTARIH`. Satır = bir kabin ya da kasa. */
export interface HamOzetSatiri {
  cins: string | null;
  bolum: string | null;
  kabin: string | number | null;
  adisyon: number | string | null;
  tutar: number | string | null;
  nakit: number | string | null;
  kart: number | string | null;
  yikama_adet: number | string | null;
  tellak_adet: number | string | null;
  /** Kalışı ölçülen adisyonların dakika TOPLAMI. */
  kalis_dk: number | string | null;
  /** Kalışı ölçülen adisyon sayısı (yalnız Salon ve VİP'te dolu). */
  kalis_adisyon: number | string | null;
  son_kapanis: string | null;
}

export interface KabinSatiri {
  /** `cins|bolum|kabin`: çekmece seçimi URL'de bununla durur. */
  kimlik: string;
  cins: Cins;
  bolum: string;
  kabin: string;
  /** Tarafsız ad: "Salon 6", "Kabinsiz satış". */
  ad: string;
  /** Taraflı ad: "Bayan Salon 6" (sıralama anahtarı ve çekmece başlığı). */
  etiket: string;
  adisyon: number;
  tutar: number;
  nakit: number;
  kart: number;
  yikama: number;
  tellak: number;
  kalisDk: number;
  kalisAdisyon: number;
  /** Kabinin ortalama kalışı (dakika); kalışı ölçülen adisyonu yoksa null. */
  ortKalis: number | null;
  sonKapanis: string | null;
}

const ortalama = (dk: number, adisyon: number) => (adisyon > 0 ? Math.round(dk / adisyon) : null);

export function kabinSatiri(h: HamOzetSatiri): KabinSatiri {
  const cins = cinsOku(h.cins);
  const bolum = metinOku(h.bolum);
  const kabin = metinOku(h.kabin);
  const ad = bolum === KABINSIZ ? 'Kabinsiz satış' : `${bolum} ${kabin}`.trim();
  const kalisDk = sayiOku(h.kalis_dk);
  const kalisAdisyon = sayiOku(h.kalis_adisyon);
  return {
    kimlik: `${cins}|${bolum}|${kabin}`,
    cins,
    bolum,
    kabin,
    ad,
    etiket: cins === '-' ? ad : `${CINS_ADI[cins]} ${ad}`,
    adisyon: sayiOku(h.adisyon),
    tutar: sayiOku(h.tutar),
    nakit: sayiOku(h.nakit),
    kart: sayiOku(h.kart),
    yikama: sayiOku(h.yikama_adet),
    tellak: sayiOku(h.tellak_adet),
    kalisDk,
    kalisAdisyon,
    ortKalis: ortalama(kalisDk, kalisAdisyon),
    sonKapanis: h.son_kapanis || null,
  };
}

export interface Olcu {
  adisyon: number;
  tutar: number;
  nakit: number;
  kart: number;
  yikama: number;
  tellak: number;
  /** Ortalama kalış (dakika); payda 0 ise null. */
  kalis: number | null;
}

/**
 * Satırların toplam ölçüsü. Özet kutuları, taraf kartları ve kabin tablosunun toplamı bununla hesaplanır.
 * Ortalama kalış Σ kalis_dk / Σ kalis_adisyon: kabin ortalamalarının ortalaması DEĞİL (az adisyonlu kabin
 * ortalamayı çekerdi).
 */
export function olcu(satirlar: readonly KabinSatiri[]): Olcu {
  let adisyon = 0, tutar = 0, nakit = 0, kart = 0, yikama = 0, tellak = 0, dk = 0, kalisli = 0;
  for (const s of satirlar) {
    adisyon += s.adisyon;
    tutar += s.tutar;
    nakit += s.nakit;
    kart += s.kart;
    yikama += s.yikama;
    tellak += s.tellak;
    dk += s.kalisDk;
    kalisli += s.kalisAdisyon;
  }
  return {
    adisyon, tutar: yuvarla2(tutar), nakit: yuvarla2(nakit), kart: yuvarla2(kart),
    yikama: yuvarla2(yikama), tellak: yuvarla2(tellak), kalis: ortalama(dk, kalisli),
  };
}

export function tarafaGoreSuz(satirlar: readonly KabinSatiri[], taraf: Taraf): KabinSatiri[] {
  return taraf === 'T' ? satirlar.slice() : satirlar.filter((s) => s.cins === taraf);
}

export function bolumeGoreSuz(satirlar: readonly KabinSatiri[], bolum: BolumSecimi): KabinSatiri[] {
  return bolum === 'T' ? satirlar.slice() : satirlar.filter((s) => s.bolum === bolum);
}

export interface BolumSatiri extends Olcu {
  /** `cins|bolum|` (kabin boş): çekmecede bölümün TÜM kabinleri açılır. */
  kimlik: string;
  ad: string;
}

export interface TarafOzeti {
  cins: Cins;
  toplam: Olcu;
  /** Giriş kasası, Salon, VİP sırasıyla; adisyonu olmayan bölüm satır olmaz. */
  bolumler: BolumSatiri[];
}

export function tarafOzeti(satirlar: readonly KabinSatiri[], cins: Cins): TarafOzeti {
  const taraf = satirlar.filter((s) => s.cins === cins);
  const bolumler: BolumSatiri[] = [];
  for (const b of KART_BOLUMLERI) {
    const o = olcu(taraf.filter((s) => s.bolum === b));
    if (o.adisyon > 0) bolumler.push({ ...o, kimlik: `${cins}|${b}|`, ad: b === 'Giriş' ? 'Giriş kasası' : b });
  }
  return { cins, toplam: olcu(taraf), bolumler };
}

/** Çekmecede açılan şey: tek kabin / kasa ya da bir tarafın bölümünün tamamı. */
export interface CekmeceHedefi {
  kimlik: string;
  cins: Cins;
  bolum: string;
  /** Boşsa `@KABIN` gönderilmez: bölümün tüm kabinleri. */
  kabin: string;
  baslik: string;
  /** Bölümün tamamı açıldıysa adisyon tablosunda "Kabin" kolonu da görünür. */
  kabinGoster: boolean;
}

/**
 * URL'deki kimlikten çekmece hedefi. Yalnız ÖZETTE satırı olan kabin ya da bölüm açılır: paylaşılan
 * bağlantıdaki kabin bu aralıkta yoksa çekmece açılmaz, sorgu atılmaz.
 */
export function hedefCoz(kimlik: string, satirlar: readonly KabinSatiri[]): CekmeceHedefi | undefined {
  if (!kimlik) return undefined;
  const satir = satirlar.find((s) => s.kimlik === kimlik);
  if (satir) {
    return { kimlik, cins: satir.cins, bolum: satir.bolum, kabin: satir.kabin, baslik: satir.etiket, kabinGoster: false };
  }
  const [c, bolum, kabin] = kimlik.split('|');
  const cins = cinsOku(c);
  if (kabin !== '' || !bolum || !satirlar.some((s) => s.cins === cins && s.bolum === bolum)) return undefined;
  const bolumAdi = bolum === 'Giriş' ? 'giriş kasası' : bolum;
  return { kimlik, cins, bolum, kabin: '', baslik: `${CINS_ADI[cins]} ${bolumAdi}`, kabinGoster: true };
}

// ── zaman: gün / saat kovaları ──────────────────────────────────────────────────

/** `zaman` ds'i, parametreler `@BASTARIH`, `@BITTARIH`, `@KIRILIM` ('gun' | 'saat'). */
export interface HamZamanSatiri {
  gun: string | null;
  /** 0-23; kırılım 'gun' iken null. */
  saat: number | string | null;
  cins: string | null;
  tutar: number | string | null;
  adisyon: number | string | null;
}

export interface ZamanKovasi {
  anahtar: string;
  /** Eksen etiketi: gün "03.10", saat "14". */
  etiket: string;
  /** Gün kovasında ISO gün (basınca dönem o güne iner); saat kovasında null. */
  gun: string | null;
  bayan: number;
  erkek: number;
  kabinsiz: number;
  toplam: number;
}

/** Bu kadar günden uzun aralıkta boş günler kova olmaz (çubuklar okunmaz hâle gelirdi). */
const BOS_GUN_SINIRI = 92;

/**
 * Çubukların kovaları. Tek gün seçiliyse SAAT (ilk ve son satışlı saat arası), değilse aralıktaki her
 * gün. Tek günde saati boş satır (dönem değişirken ekranda kalan önceki gün kırılımı) kovaya girmez.
 */
export function zamanKovalari(ham: readonly HamZamanSatiri[], bas: string, bit: string, taraf: Taraf): ZamanKovasi[] {
  const tekGun = bas === bit;
  const harita = new Map<string, ZamanKovasi>();
  const kova = (anahtar: string, etiket: string, gun: string | null) => {
    let k = harita.get(anahtar);
    if (!k) {
      k = { anahtar, etiket, gun, bayan: 0, erkek: 0, kabinsiz: 0, toplam: 0 };
      harita.set(anahtar, k);
    }
    return k;
  };
  const gunEtiketi = (g: string) => `${g.slice(8, 10)}.${g.slice(5, 7)}`;

  for (const h of ham) {
    const cins = cinsOku(h.cins);
    if (taraf !== 'T' && cins !== taraf) continue;
    const gun = (h.gun ?? '').slice(0, 10);
    if (gun < bas || gun > bit) continue;
    let k: ZamanKovasi;
    if (tekGun) {
      if (h.saat === null || h.saat === undefined || h.saat === '') continue;
      const saat = iki(sayiOku(h.saat));
      k = kova(saat, saat, null);
    } else {
      k = kova(gun, gunEtiketi(gun), gun);
    }
    const tutar = sayiOku(h.tutar);
    if (cins === 'K') k.bayan += tutar;
    else if (cins === 'E') k.erkek += tutar;
    else k.kabinsiz += tutar;
  }

  const anahtarlar: string[] = [];
  if (tekGun) {
    const dolu = Array.from(harita.keys()).sort();
    if (dolu.length > 0) {
      for (let s = Number(dolu[0]); s <= Number(dolu[dolu.length - 1]); s++) anahtarlar.push(iki(s));
    }
  } else {
    for (let g = bas, n = 0; g <= bit && n <= BOS_GUN_SINIRI; g = gunEkle(g, 1), n++) anahtarlar.push(g);
    if (anahtarlar[anahtarlar.length - 1] !== bit) {
      anahtarlar.length = 0;
      anahtarlar.push(...Array.from(harita.keys()).sort());
    }
  }
  return anahtarlar.map((a) => {
    const k = tekGun ? kova(a, a, null) : kova(a, gunEtiketi(a), a);
    k.bayan = yuvarla2(k.bayan);
    k.erkek = yuvarla2(k.erkek);
    k.kabinsiz = yuvarla2(k.kabinsiz);
    k.toplam = yuvarla2(k.bayan + k.erkek + k.kabinsiz);
    return k;
  });
}

// ── adisyonlar (çekmece) ────────────────────────────────────────────────────────

/** `adisyonlar` ds'i, parametreler `@BASTARIH`, `@BITTARIH`, `@CINS`, `@BOLUM`, `@KABIN`. */
export interface HamAdisyonSatiri {
  /** 19 haneli kimlik, METİN gelir. Sayıya ÇEVRİLMEZ (JS sayısı 15-16 haneden sonrasını yuvarlar). */
  adisyon: string;
  kabin: string | number | null;
  acilis: string | null;
  kapanis: string | null;
  kalis_dk: number | string | null;
  kalem_sayisi: number | string | null;
  nakit: number | string | null;
  kart: number | string | null;
  tutar: number | string | null;
}

export interface AdisyonSatiri {
  adisyon: string;
  kabin: string;
  acilis: string | null;
  kapanis: string | null;
  /** Kalış (dakika); ölçülmediyse (giriş kasası, kabinsiz, sıfır süre) null. */
  kalis: number | null;
  kalemSayisi: number;
  nakit: number;
  kart: number;
  tutar: number;
}

export interface AdisyonOzeti {
  /** Son kapanandan başlayarak. */
  adisyonlar: AdisyonSatiri[];
  tutar: number;
  nakit: number;
  kart: number;
  kalem: number;
  /** Kalışı ölçülen adisyonların ortalaması; hiçbiri ölçülmediyse null. */
  kalis: number | null;
}

/** Adisyonların ortalama kalışı: yalnız kalışı ölçülenler paydaya girer. */
export function ortalamaKalis(adisyonlar: readonly AdisyonSatiri[]): number | null {
  let dk = 0, n = 0;
  for (const a of adisyonlar) {
    if (a.kalis !== null) {
      dk += a.kalis;
      n += 1;
    }
  }
  return ortalama(dk, n);
}

export function adisyonOzeti(ham: readonly HamAdisyonSatiri[]): AdisyonOzeti {
  const adisyonlar = ham.map<AdisyonSatiri>((h) => {
    const kalis = sayiOku(h.kalis_dk);
    return {
      adisyon: metinOku(h.adisyon),
      kabin: metinOku(h.kabin),
      acilis: h.acilis || null,
      kapanis: h.kapanis || null,
      kalis: kalis > 0 ? kalis : null,
      kalemSayisi: sayiOku(h.kalem_sayisi),
      nakit: sayiOku(h.nakit),
      kart: sayiOku(h.kart),
      tutar: sayiOku(h.tutar),
    };
  });
  adisyonlar.sort((a, b) => (b.kapanis ?? '').localeCompare(a.kapanis ?? ''));
  let tutar = 0, nakit = 0, kart = 0, kalem = 0;
  for (const a of adisyonlar) {
    tutar += a.tutar;
    nakit += a.nakit;
    kart += a.kart;
    kalem += a.kalemSayisi;
  }
  return { adisyonlar, tutar: yuvarla2(tutar), nakit: yuvarla2(nakit), kart: yuvarla2(kart), kalem, kalis: ortalamaKalis(adisyonlar) };
}

// ── kalemler (modal) ────────────────────────────────────────────────────────────

/** `kalemler` ds'i (canlıdaki "Kabin Adisyonu"), parametre `@ADISYON` (metin). */
export interface HamKalemSatiri {
  kalem: string | null;
  adet: number | string | null;
  tutar: number | string | null;
  saat: string | null;
}

export interface KalemSatiri {
  sira: number;
  /** 'HH:MM'. */
  saat: string;
  kalem: string;
  adet: number;
  tutar: number;
}

export function kalemSatirlari(ham: readonly HamKalemSatiri[]): KalemSatiri[] {
  return ham.map((h, sira) => ({
    sira,
    saat: saatMetni(h.saat),
    kalem: metinOku(h.kalem),
    adet: sayiOku(h.adet),
    tutar: sayiOku(h.tutar),
  }));
}

// ── satılanlar ──────────────────────────────────────────────────────────────────

/** `satilan` ds'i, parametreler `@BASTARIH`, `@BITTARIH`. Satır = grup + ürün + cins. */
export interface HamSatilanSatiri {
  grup: string | null;
  urun: string | null;
  cins: string | null;
  adet: number | string | null;
  tutar: number | string | null;
}

export interface UrunSatiri {
  kimlik: string;
  grup: string;
  urun: string;
  adet: number;
  tutar: number;
  /** Tutarın Bayan tarafında satılan kısmı. */
  bayan: number;
  erkek: number;
}

/**
 * Ürün bazında toplam (tablo grup satırlarını ve ara toplamları bundan kurar). Taraf süzgeci etkinse
 * yalnız o cinsin satırları toplanır; "Tümü"nde kabinsiz satış Adet ve Tutar'a girer, Bayan / Erkek
 * kolonlarına girmez. Tutara göre azalan.
 */
export function satilanUrunler(ham: readonly HamSatilanSatiri[], taraf: Taraf): UrunSatiri[] {
  const harita = new Map<string, UrunSatiri>();
  for (const h of ham) {
    const cins = cinsOku(h.cins);
    if (taraf !== 'T' && cins !== taraf) continue;
    const grup = metinOku(h.grup) || 'Diğer';
    const urun = metinOku(h.urun);
    const kimlik = `${grup}|${urun}`;
    let u = harita.get(kimlik);
    if (!u) {
      u = { kimlik, grup, urun, adet: 0, tutar: 0, bayan: 0, erkek: 0 };
      harita.set(kimlik, u);
    }
    const tutar = sayiOku(h.tutar);
    u.adet += sayiOku(h.adet);
    u.tutar += tutar;
    if (cins === 'K') u.bayan += tutar;
    else if (cins === 'E') u.erkek += tutar;
  }
  const urunler = Array.from(harita.values());
  for (const u of urunler) {
    u.adet = yuvarla2(u.adet);
    u.tutar = yuvarla2(u.tutar);
    u.bayan = yuvarla2(u.bayan);
    u.erkek = yuvarla2(u.erkek);
  }
  return urunler.sort((a, b) => b.tutar - a.tutar);
}
